import {
    applyCouponCode,
    applyItemLevelOffers,
    applyOrderLevelOffer,
    refreshCalculation,
} from "./discount_engine";
import { computeCheckoutTotals, transformItems } from "./order_helper";

// Route an offer (or list of offers) through the correct engine API and
// dispatch the result into Redux under SET_ENGINE_DISCOUNT. Callers pass
// their items + a small ctx so this stays shape-agnostic.
//
// offerOrOffers: single offer object or an array. Arrays only matter for
//   entryPoint "auto" with item-level (C1/C3) offers — applyItemLevelOffers
//   tries every combination and returns the cart with maximum aggregate
//   discount (OfferEngineImpl.applyC3Coupon in the engine). For "coupon" and
//   "list" the user chose one specific offer, so only the first entry is used.
//
// ctx = { orderType, storeId, paymentOption, storeConfig, itemsAreFlat, tip, entryPoint }
export const routeOfferThroughEngine = async (offerOrOffers, items, ctx, dispatch) => {
    try {
        const offersList = Array.isArray(offerOrOffers) ? offerOrOffers.filter(Boolean) : [offerOrOffers].filter(Boolean);
        if (!offersList.length) return null;
        const primaryOffer = offersList[0];

        const cleanItems = (items || []).map((it) => {
            const { discount, ...rest } = it;
            return rest;
        });
        const tipAmount = parseFloat(ctx?.tip || 0) || 0;
        const calculated = computeCheckoutTotals(cleanItems, {
            orderType:     ctx?.orderType,
            storeId:       ctx?.storeId,
            paymentOption: ctx?.paymentOption,
            storeConfig:   ctx?.storeConfig,
            itemsAreFlat:  ctx?.itemsAreFlat === true,
            tip:           tipAmount,
        });
        const cartOrder = {
            ...calculated,
            order_type: ctx?.orderType || calculated?.order_type || "Dine In",
            tip_amount: tipAmount,
            charges_data: Array.isArray(calculated?.charges_data) ? calculated.charges_data : [],
        };
        if (cartOrder.invoice_no === "" || isNaN(Number(cartOrder.invoice_no))) {
            delete cartOrder.invoice_no;
        }
        if (cartOrder.offline_invoice_no === "" || cartOrder.offline_invoice_no == null) {
            delete cartOrder.offline_invoice_no;
        }

        const entryPoint = ctx?.entryPoint || "auto";
        const isCouponEntry = entryPoint === "coupon";
        // Item-level detection must consult offering_type/offering_typecode
        // first — term_style alone lies. Offer 1070 has term_style "C2" but
        // offering_type "Transaction" (offering_typecode "2"): it's a flat
        // ₹200 off ≥ ₹2000, not an item-level C2. Routing it through
        // applyItemLevelOffers made the engine scan item eligibility against
        // an empty offer_term_product and stamp zero discount. Coupon entry
        // is fine because it bypasses this check entirely via applyCouponCode.
        const isItemLevelOffer = (o) => {
            const term = o?.offer_term?.[0] || {};
            const otype = term.offering_type;
            const otypecode = String(term.offering_typecode || "");
            if (otype === "Transaction" || otypecode === "2") return false;
            if (otype === "Item" || otypecode === "1") return true;
            if (o?.entity === "ITEM") return true;
            const ts = o?.offer_header?.term_style;
            return ts === "C3" || ts === "C2";
        };
        const primaryIsItemLevel = isItemLevelOffer(primaryOffer);
        const useCouponApi = isCouponEntry;
        const useItemApi   = !useCouponApi && primaryIsItemLevel;

        // Coupon/list keep single-offer semantics. Auto + item-level passes
        // every eligible item-level offer so the engine can pick the best
        // combination.
        const offersForEngine = useCouponApi
            ? [primaryOffer]
            : (useItemApi ? offersList.filter(isItemLevelOffer) : [primaryOffer]);

        // Item-level engine API rejects offers with non-empty coupon_code
        // (Branch B admits C1/C3 with null/empty only), and C2 needs
        // term_style normalized to C1.
        const normalizeForItemApi = (o) => ({
            ...o,
            offer_header: {
                ...(o.offer_header || {}),
                term_style: o?.offer_header?.term_style === "C2" ? "C1" : (o?.offer_header?.term_style),
            },
            offer_term: (o.offer_term || []).map((t) => ({ ...t, coupon_code: "" })),
        });
        const engineOffers = useItemApi ? offersForEngine.map(normalizeForItemApi) : offersForEngine;
        const couponCode = primaryOffer?.offer_term?.[0]?.coupon_code || "";
        const apiName = useCouponApi
            ? "applyCouponCode"
            : (useItemApi ? "applyItemLevelOffers" : "applyOrderLevelOffer");
        console.log("[offerEngine IN]", {
            entryPoint,
            api: apiName,
            offers_in: offersList.map(o => ({ id: o?.offer_id, term_style: o?.offer_header?.term_style })),
            offers_to_engine: engineOffers.map(o => ({ id: o?.offer_id, term_style: o?.offer_header?.term_style })),
            cartOrder,
        });

        const result = useCouponApi
            ? await applyCouponCode(cartOrder, [engineOffers[0]], couponCode)
            : (useItemApi
                ? await applyItemLevelOffers(cartOrder, engineOffers)
                : await applyOrderLevelOffer(cartOrder, engineOffers[0]));
        console.log("[offerEngine OUT totals]", {
            applied: result?.applied,
            total_discount: result?.order?.total_discount,
            discount_amount: result?.order?.discount_amount,
            discount_percent: result?.order?.discount_percent,
            sub_total: result?.order?.sub_total,
            transaction_grand_amount: result?.order?.transaction_grand_amount,
            applied_discount: result?.order?.applied_discount,
            item_discounts: result?.order?.item_details?.map(i => ({ name: i.item_name, discount_amount: i.discount_amount })),
        });
        console.log("[offerEngine OUT full result]", JSON.parse(JSON.stringify(result || {})));
        console.log("[offerEngine OUT full order]", JSON.parse(JSON.stringify(result?.order || {})));
        console.log("[offerEngine OUT applied_discount[]]", JSON.parse(JSON.stringify(result?.order?.applied_discount || [])));
        console.log("[offerEngine OUT item_details full]", (result?.order?.item_details || []).map(i => ({
            name: i.item_name,
            sku: i.sku,
            quantity: i.quantity,
            extended_amount: i.extended_amount,
            sub_total: i.sub_total,
            total_gross_sale: i.total_gross_sale,
            total_net_sale: i.total_net_sale,
            total_item_tax: i.total_item_tax,
            discount_amount: i.discount_amount,
            discount_percent: i.discount_percent,
            total_discount: i.total_discount,
            total_order_discount: i.total_order_discount,
            applied_discount: i.applied_discount,
            offer_term: i.offer_term,
            offer_id: i.offer_id,
        })));

        let updated = result?.order;
        if (!updated) {
            console.warn("[offerEngine] no updated order returned — dispatch skipped");
            return null;
        }

        // Engine's apply*Offer APIs stamp discount metadata (per-item
        // discount_amount, order-level applied_discount[], per-item
        // applied_discount) but do NOT roll those into order-level
        // total_discount / transaction_grand_amount. Run refreshCalculation
        // with resetDiscount:false unconditionally to fold the stamped
        // discount into order totals — when there's no discount stamped it's
        // a no-op recomputation of the same totals.
        // We can't gate on result.applied: applyItemLevelOffers returns
        // `applied: undefined` for C3 configs even when it correctly stamped
        // per-item discounts, and skipping the fold leaves
        // engineDiscount.total_discount at 0 in Redux — which then trips
        // buildSaveOrderPayload's guard and drops the offer from save-order.
        //
        // Re-flatten before refresh: applyItemLevelOffers collapses addons
        // into parent.selectedAddonsItems, but refreshCalculation's
        // calculateItemPrice reads addon price from item_details[index+1]
        // (engine line 22517-22522). Post-collapse, index+1 is the next
        // parent, so its price gets written into the current parent's addon
        // slot — corrupting parent sub_total. transformItems with
        // preserveDiscount:true re-emits nested addons as sibling rows while
        // keeping the engine-stamped per-item discount_amount intact.
        const reflattened = transformItems(updated.item_details || [], 0, { preserveDiscount: true });
        const reflattenedOrder = { ...updated, item_details: reflattened };
        if (reflattenedOrder.invoice_no === "" || isNaN(Number(reflattenedOrder.invoice_no))) {
            delete reflattenedOrder.invoice_no;
        }
        if (reflattenedOrder.offline_invoice_no === "" || reflattenedOrder.offline_invoice_no == null) {
            delete reflattenedOrder.offline_invoice_no;
        }
        const recalculated = refreshCalculation(reflattenedOrder, { resetDiscount: false });
        console.log("[offerEngine after refreshCalculation]", {
            total_discount: recalculated?.total_discount,
            discount_amount: recalculated?.discount_amount,
            sub_total: recalculated?.sub_total,
            transaction_grand_amount: recalculated?.transaction_grand_amount,
        });
        if (recalculated) updated = recalculated;

        // Collect the offer_ids the engine actually stamped so save-order and
        // UI know which offers are live (multi-offer path may apply different
        // offers to different items). Per-item applied_discount is a single
        // object (not an array); order-level applied_discount is an array.
        const stampedIds = new Set();
        (updated.item_details || []).forEach(i => {
            const ad = i.applied_discount;
            if (!ad) return;
            const list = Array.isArray(ad) ? ad : [ad];
            list.forEach(x => { if (x?.offer_id) stampedIds.add(x.offer_id); });
        });
        (updated.applied_discount || []).forEach(ad => {
            if (ad?.offer_id) stampedIds.add(ad.offer_id);
        });
        const appliedOfferIds = Array.from(stampedIds);

        // Include discount_amount / discount_percent — refreshCalculationJs on
        // replay reads these top-level fields to redistribute discount per
        // item (engine: ItemPriceCalculator.a2y at line 1670-1677 in
        // CalculationEngine-sharedLogic.mjs). Without them the replay treats
        // the order as un-discounted and doubles tax + drops total_discount.
        const payload = {
            applied_discount: updated.applied_discount || [],
            total_discount: updated.total_discount || 0,
            discount_amount: updated.discount_amount || 0,
            discount_percent: updated.discount_percent || 0,
            sub_total: updated.sub_total || 0,
            total_item_tax: updated.total_item_tax || 0,
            total_net_sale: updated.total_net_sale || 0,
            total_gross_sale: updated.total_gross_sale || 0,
            transaction_grand_amount: updated.transaction_grand_amount || 0,
            charges: updated.charges || [],
            charges_data: updated.charges_data || [],
            item_details: updated.item_details || [],
            offer_id: appliedOfferIds[0] || primaryOffer?.offer_id,
            offer_ids: appliedOfferIds,
        };
        console.log("[offerEngine DISPATCH]", payload);
        dispatch({ type: "SET_ENGINE_DISCOUNT", payload });
        return payload;
    } catch (e) {
        console.error("[offer_router] engine offer routing failed", e);
        return null;
    }
};
